// Logic for the contract dashboard - see doc/contract-motivation-concept.org.
//
// Everything here is pure and the clock is always a parameter (`now`, a
// millisecond timestamp, or `today`, a Date) - exactly like lib/focus.js - so
// all of it unit-tests without rendering and without freezing timers.
//
// A contract is never stored on its own: it lives as properties of the
// `* Tagesvertrag` node inside that day's daily note (see lib/contract_org.js),
// and nowhere else. This module only shapes and evaluates the
// `{ negotiatedAt, renegotiations, points }` object that node is read into and
// written from - one day at a time, never a collection of days.

// A contract is a short list of specific points (concept §2.1); six is the hard
// ceiling, three the suggestion.
export const MAX_CONTRACT_POINTS = 6;
export const SUGGESTED_CONTRACT_POINTS = 3;

// The "way to success" row: the seven days before today, plus today itself.
export const KEPT_DAYS_SHOWN = 7;

// Pace runs against a working window, not the 24h clock (concept §3).
export const DEFAULT_DAY_START_HOUR = 8;
export const DEFAULT_DAY_END_HOUR = 18;

const pad = (number) => `${number}`.padStart(2, '0');

// INFO: Local date parts, never toISOString() - that formats in UTC and files
// the evening's ticks under tomorrow east of Greenwich. Same trap as documented
// in lib/dashboard.js.
export const dayKey = (date) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

const isValidPoint = (point) => !!point && typeof point.text === 'string' && !!point.text.trim();

const normalizePoint = (point) => ({
  text: point.text.trim(),
  done: !!point.done,
  doneAt: point.done && typeof point.doneAt === 'number' ? point.doneAt : null,
});

/**
 * Whatever was read out of a daily note (or nothing at all) into the one shape
 * the rest of this module works on, or null if there is nothing usable.
 *
 * Junk points are dropped rather than rendered - the daily note is hand-editable,
 * and one broken property must not take down the page.
 */
export const normalizeContract = (contract) => {
  if (!contract || !Array.isArray(contract.points)) return null;

  const points = contract.points.filter(isValidPoint).map(normalizePoint);
  if (!points.length) return null;

  return {
    negotiatedAt: typeof contract.negotiatedAt === 'number' ? contract.negotiatedAt : null,
    renegotiations: typeof contract.renegotiations === 'number' ? contract.renegotiations : 0,
    points,
  };
};

/**
 * Sign (or renegotiate) a day's contract.
 *
 * Renegotiation replaces the points but keeps the tick of any point whose text is
 * unchanged - retyping a promise must not un-fulfil it. It also increments the
 * visible `renegotiations` counter: allowed under autonomy (concept §2.6), but a
 * contract silently rewritten to match reality would make the record worthless.
 *
 * Returns null if there is nothing left to sign (every point was blank).
 */
export const negotiateContract = (existing, { points, now }) => {
  const normalizedExisting = normalizeContract(existing);

  const previousByText = {};
  ((normalizedExisting && normalizedExisting.points) || []).forEach((point) => {
    previousByText[point.text] = point;
  });

  const nextPoints = (points || [])
    .map((text) => `${text || ''}`.trim())
    .filter((text) => !!text)
    .slice(0, MAX_CONTRACT_POINTS)
    .map((text) => {
      const previous = previousByText[text];
      return previous
        ? { text, done: previous.done, doneAt: previous.doneAt }
        : { text, done: false, doneAt: null };
    });

  if (!nextPoints.length) return null;

  return {
    negotiatedAt: normalizedExisting ? normalizedExisting.negotiatedAt : now,
    renegotiations: normalizedExisting ? normalizedExisting.renegotiations + 1 : 0,
    points: nextPoints,
  };
};

/** Tick or untick one point of a contract; the tick remembers when it happened. */
export const togglePoint = (contract, index, now) => {
  const normalized = normalizeContract(contract);
  if (!normalized || !normalized.points[index]) return normalized;

  const points = normalized.points.map((point, pointIndex) => {
    if (pointIndex !== index) return point;

    return point.done
      ? { ...point, done: false, doneAt: null }
      : { ...point, done: true, doneAt: now };
  });

  return { ...normalized, points };
};

export const isKept = (contract) =>
  !!contract && contract.points.length > 0 && contract.points.every((p) => p.done);

/**
 * How much of the working window has passed, clamped to [0, 1].
 *
 * Before the window opens the fraction is 0 - nothing is due yet; after it closes
 * it is 1 - everything is. A degenerate window falls back to the default one
 * rather than dividing by zero.
 */
export const dayWindowFraction = (now, startHour, endHour) => {
  let start = Number(startHour);
  let end = Number(endHour);

  if (!Number.isFinite(start) || !Number.isFinite(end) || start >= end || start < 0 || end > 24) {
    start = DEFAULT_DAY_START_HOUR;
    end = DEFAULT_DAY_END_HOUR;
  }

  const at = new Date(now);
  const dayStart = new Date(at.getFullYear(), at.getMonth(), at.getDate(), start).getTime();
  const dayEnd = new Date(at.getFullYear(), at.getMonth(), at.getDate(), end).getTime();

  return Math.min(1, Math.max(0, (now - dayStart) / (dayEnd - dayStart)));
};

/**
 * Today's evaluation, relative to the time passed (concept §4.3).
 *
 * Expected progress is fraction * pointCount. Statuses are deliberately only
 * 'kept' | 'onPace' | 'behind' - task-focused, per Kluger & DeNisi (concept §2.7):
 * the numbers say how the day is running, never anything about the person.
 */
export const paceFor = (contract, fraction) => {
  const points = (contract && contract.points) || [];
  const pointCount = points.length;
  const doneCount = points.filter((point) => point.done).length;
  const expected = fraction * pointCount;
  // The epsilon keeps "2 done of 4 at exactly half time" on pace instead of letting
  // float noise round it to behind.
  const behindBy = Math.max(0, Math.ceil(expected - doneCount - 1e-9));

  return {
    pointCount,
    doneCount,
    expected,
    behindBy,
    status: pointCount > 0 && doneCount >= pointCount ? 'kept' : behindBy > 0 ? 'behind' : 'onPace',
  };
};

/**
 * The seven days before `today`, oldest first, each with its verdict - derived
 * here and never stored (concept §3).
 *
 * `contractsByKey` maps a 'YYYY-MM-DD' key to that day's contract (or to
 * anything falsy, for a day whose note has no contract - or no note at all).
 */
export const summarizeDays = (contractsByKey, today, count = KEPT_DAYS_SHOWN) => {
  const byKey = contractsByKey || {};
  const summaries = [];

  for (let offset = count; offset >= 1; offset -= 1) {
    const date = new Date(today.getFullYear(), today.getMonth(), today.getDate() - offset);
    const key = dayKey(date);
    const contract = normalizeContract(byKey[key]);

    summaries.push({
      date,
      key,
      negotiated: !!contract,
      kept: isKept(contract),
      doneCount: contract ? contract.points.filter((point) => point.done).length : 0,
      pointCount: contract ? contract.points.length : 0,
    });
  }

  return summaries;
};

/**
 * Consecutive kept contracts, counted back from today.
 *
 * A day without a contract is a rest day: skipped, never a break (concept §4.4).
 * A negotiated but unkept day ends the run. Today only counts once it is kept -
 * an unfinished today never breaks the run either.
 */
export const keptRun = (summaries, todayContract = null) => {
  let run = isKept(todayContract) ? 1 : 0;

  for (let index = (summaries || []).length - 1; index >= 0; index -= 1) {
    const summary = summaries[index];

    if (summary.kept) {
      run += 1;
    } else if (summary.negotiated) {
      break;
    }
  }

  return run;
};

/** "5 of 7 under contract, 4 kept" - avoidance stays visible without shaming. */
export const reliability = (summaries) => {
  const negotiated = (summaries || []).filter((summary) => summary.negotiated);

  return {
    negotiatedCount: negotiated.length,
    keptCount: negotiated.filter((summary) => summary.kept).length,
  };
};
